import React, { useState, useEffect } from 'react';
import Header from './components/Header';
import LoadingScreen from './components/LoadingScreen';
import ThreeScene from './components/ThreeScene';
import ServiceModal from './components/ServiceModal';
import ShowreelModal from './components/ShowreelModal';
import VideoPlayer from './components/VideoPlayer';
import Contact from './components/Contact';
import About from './components/About';
import MobileNav from './components/MobileNav';
import './styles/App.css';

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [currentSection, setCurrentSection] = useState('home');
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [isShowreelModalOpen, setIsShowreelModalOpen] = useState(false);
  const [isVideoPlayerOpen, setIsVideoPlayerOpen] = useState(false);
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  const [showRemoteGuide, setShowRemoteGuide] = useState(false);
  const [cinematicMode, setCinematicMode] = useState(true);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 900);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLoadingComplete = () => {
    setIsLoading(false);
    // Don't show remote guide popup on startup in cinematic mode
    // It will only show when manually toggling to non-cinematic mode
  };

  const handleServiceClick = (service) => {
    setSelectedService(service);
    setIsServiceModalOpen(true);
  };

  const handleButtonClick = (service) => {
    setSelectedService(service);
    setIsVideoPlayerOpen(true);
  };

  const handleNavigateToSection = (section) => {
    setCurrentSection(section);
    setIsMobileNavOpen(false);
    
    // Handle promo videos section - trigger video player
    if (section === 'promo') {
      const promoService = {
        id: 'promo',
        title: 'Promo Videos',
        description: 'Our promotional videos are designed to create an immediate impact, delivering your message with cinematic quality that captures attention and drives engagement.',
        features: [
          'High-end visual aesthetics',
          'Strategic storytelling',
          'Platform-optimized delivery',
          'Performance-focused content'
        ]
      };
      setSelectedService(promoService);
      setIsVideoPlayerOpen(true);
    }
    
    // Handle corporate videos section - trigger video player
    if (section === 'corporate') {
      const corporateService = {
        id: 'corporate',
        title: 'Corporate Videos',
        description: 'Professional corporate video production that elevates your brand and communicates your message with clarity and impact.',
        features: [
          'Professional presentation',
          'Brand-focused storytelling',
          'High-quality production',
          'Corporate communication'
        ]
      };
      setSelectedService(corporateService);
      setIsVideoPlayerOpen(true);
    }
    
    // Handle music videos section - trigger video player
    if (section === 'music') {
      const musicService = {
        id: 'music',
        title: 'Music Videos',
        description: 'Creative music video production that brings your sound to life with stunning visuals and artistic storytelling.',
        features: [
          'Creative visual concepts',
          'Artistic storytelling',
          'High-quality cinematography',
          'Music-focused production'
        ]
      };
      setSelectedService(musicService);
      setIsVideoPlayerOpen(true);
    }
    
    // Handle film production section - trigger video player
    if (section === 'film') {
      const filmService = {
        id: 'film',
        title: 'Film Production',
        description: 'Professional film production services that bring your cinematic vision to life with exceptional storytelling and visual excellence.',
        features: [
          'Cinematic storytelling',
          'Professional cinematography',
          'Post-production excellence',
          'Creative direction'
        ]
      };
      setSelectedService(filmService);
      setIsVideoPlayerOpen(true);
    }
    
    // Handle about section - trigger about modal
    if (section === 'about') {
      setIsAboutModalOpen(true);
    }
  };

  const closeModals = () => {
    setIsServiceModalOpen(false);
    setIsShowreelModalOpen(false);
    setIsVideoPlayerOpen(false);
    setIsAboutModalOpen(false);
    setSelectedService(null);
  };

  const handleContactClick = () => {
    closeModals();
    setCurrentSection('contact');
  };

  const handleHelpClick = () => {
    setShowRemoteGuide(true);
    // Hide the popup after 8 seconds
    setTimeout(() => {
      setShowRemoteGuide(false);
    }, 8000);
  };

  const handleCloseRemoteGuide = () => {
    setShowRemoteGuide(false);
  };

  const handleToggleCinematicMode = (enabled) => {
    const wasInCinematicMode = cinematicMode;
    setCinematicMode(enabled);
    // Show helper dialog only when actively switching away from cinematic mode (not on startup)
    if (wasInCinematicMode && !enabled) {
      setShowRemoteGuide(true);
      // Hide the popup after 8 seconds
      setTimeout(() => {
        setShowRemoteGuide(false);
      }, 8000);
    }
  };

  return (
    <div className="App">
      {isLoading && (
        <LoadingScreen onLoadingComplete={handleLoadingComplete} />
      )}
      
      <Header 
        onNavigate={handleNavigateToSection}
        onToggleMobileNav={() => setIsMobileNavOpen(!isMobileNavOpen)}
        isMobileNavOpen={isMobileNavOpen}
        cinematicMode={cinematicMode}
        onToggleCinematicMode={handleToggleCinematicMode}
      />
      
      <MobileNav 
        isOpen={isMobileNavOpen}
        onNavigate={handleNavigateToSection}
        onClose={() => setIsMobileNavOpen(false)}
      />
      
      <main>
        <ThreeScene 
          currentSection={currentSection}
          onServiceClick={handleServiceClick}
          onButtonClick={handleButtonClick}
          onShowreelClick={() => setIsShowreelModalOpen(true)}
          onHelpClick={handleHelpClick}
          isMobile={isMobile}
          isModalOpen={isServiceModalOpen || isShowreelModalOpen || isVideoPlayerOpen}
          cinematicMode={cinematicMode}
        />
        
        <div className="overlay">
          {cinematicMode ? (
            <>
              <div className="hero-content cinematic">
                <h1><span className="black-text">Cinematic</span> <span className="black-text">Storytelling</span></h1>
                <p className="black-text">We create powerful visual narratives that captivate and inspire</p>
                <button 
                  className="cta-button" 
                  onClick={() => setIsShowreelModalOpen(true)}
                >
                  View Showreel
                </button>
              </div>
              
              <div className="cinematic-navigation">
              <div className="cinematic-nav-box" onClick={() => handleNavigateToSection('promo')}>
                <div className="video-preview">
                  <video 
                    src="/Promo.mp4" 
                    muted 
                    loop 
                    autoPlay 
                    playsInline
                    className="preview-video"
                  />
                </div>
                <span>Promo Videos</span>
              </div>
              <div className="cinematic-nav-box" onClick={() => handleNavigateToSection('music')}>
                <div className="video-preview">
                  <video 
                    src="/MusicVideos.mp4" 
                    muted 
                    loop 
                    autoPlay 
                    playsInline
                    className="preview-video"
                  />
                </div>
                <span>Music Videos</span>
              </div>
              <div className="cinematic-nav-box" onClick={() => handleNavigateToSection('corporate')}>
                <div className="video-preview">
                  <video 
                    src="/Corparate.mp4" 
                    muted 
                    loop 
                    autoPlay 
                    playsInline
                    className="preview-video"
                  />
                </div>
                <span>Corporate</span>
              </div>
              <div className="cinematic-nav-box" onClick={() => handleNavigateToSection('film')}>
                <div className="video-preview">
                  <video 
                    src="/Film.mp4" 
                    muted 
                    loop 
                    autoPlay 
                    playsInline
                    className="preview-video"
                  />
                </div>
                <span>Film Production</span>
              </div>
            </div>

            <div className="cinematic-nav-footer">
              <button
                type="button"
                className="cinematic-nav-footer-link"
                onClick={() => handleNavigateToSection('about')}
              >
            
              </button>
              <div className="cinematic-nav-footer-dev">
                <span className="cinematic-nav-footer-text">Our Development Department:</span>
                <a
                  className="cinematic-nav-footer-link"
                  href="https://jfkproductionstech.com"
                  target="_blank"
                  rel="noreferrer"
                >
                  jfkproductionstech.com
                </a>
              </div>
            </div>
            </>
          ) : (
            <>
              <div className="hero-content non-cinematic">
                <h1>JFK <span>Productions</span></h1>
                <p>Professional video production services with cutting-edge technology</p>
                <button 
                  className="cta-button" 
                  onClick={() => setIsShowreelModalOpen(true)}
                >
                  View Showreel
                </button>
              </div>
              
              <div className="scroll-indicator">
                <p>Scroll to Explore</p>
                <div className="scroll-arrow">↓</div>
              </div>
            </>
          )}
        </div>
        
        <div className="section-indicator">
          <p>{currentSection.charAt(0).toUpperCase() + currentSection.slice(1)}</p>
        </div>
        
        <div className="controls-info">
          <p>WASD to move | Mouse to look | Click on objects to explore</p>
        </div>
      </main>
      
      {isServiceModalOpen && selectedService && (
        <ServiceModal 
          service={selectedService}
          onClose={closeModals}
          onContactClick={handleContactClick}
        />
      )}
      
      {isShowreelModalOpen && (
        <ShowreelModal onClose={closeModals} />
      )}
      
      {/* Video Player - overlaid on the scene */}
      {isVideoPlayerOpen && selectedService && (
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 50 }}>
          <div style={{ pointerEvents: 'auto' }}>
            <VideoPlayer 
              service={selectedService}
              onClose={closeModals}
            />
          </div>
        </div>
      )}
      
      <Contact 
        isVisible={currentSection === 'contact'}
        onClose={() => setCurrentSection('home')}
      />
      
      <About 
        isVisible={isAboutModalOpen}
        onClose={() => setIsAboutModalOpen(false)}
      />
      
      {/* Remote Guide Popup */}
      {showRemoteGuide && (
        <div className="remote-guide-popup">
          <div className="remote-guide-content">
            <button className="guide-close-button" onClick={handleCloseRemoteGuide}>
              ×
            </button>
            <h3>🎮 Navigation Guide</h3>
            <p>{cinematicMode ? 'Click on the navigation boxes at the bottom to explore our content!' : 'Click on the 3D remote control to operate the television!'}</p>
            <div className="guide-instructions">
              <div className="instruction-item">
                <span className="button-highlight">PROMO VIDEOS</span> - View our promotional content
              </div>
              <div className="instruction-item">
                <span className="button-highlight">CORPORATE VIDEOS</span> - Professional business content
              </div>
              <div className="instruction-item">
                <span className="button-highlight">MUSIC VIDEOS</span> - Creative music productions
              </div>
              <div className="instruction-item">
                <span className="button-highlight">FILM PRODUCTION</span> - Cinematic storytelling
              </div>
            </div>
            <div className="cinematic-mode-info">
              <div className="instruction-item">
                <span className="button-highlight">CINEMATIC MODE</span> - Toggle for immersive cinema experience with navigation boxes
              </div>
            </div>
            <p className="guide-tip">💡 Tip: {cinematicMode ? 'Use the toggle in the header to switch between modes!' : 'Hover over the remote buttons to see your cursor change!'}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
